<template>
  <billing-dialog
    :visible.sync="visible"
    @close="handleCancel"
    @back-event="handleCancel" >
    <h1 class="opc-zeduschp-comparison-dialog__title">{{$t('billing.planform.title.zoom_workplace_for_edu')}}</h1>
    <ZoomOneEduPlanComparison appName="opc" :colConfig="colConfig" :enableDisplayZoomWorkFlow="enableDisplayZoomWorkFlow" :enableDisplayZoomDrive="enableDisplayZoomDrive" :largeMeetingConfig="lmConfig" :enableDocsFeature="enableDocsFeature" :enableDisplayZoomTasksWithoutAccountCheck="enableDisplayZoomTasksWithoutAccountCheck" :showCampusScheduler="showCampusScheduler" :isEligibleAccount4CustomAvatarFeature="isEligibleAccount4CustomAvatarFeature">
      <template #comparison-table-header="{ col }">
        <div class="opc-zeduschp-comparison-header">
          <p class="opc-zeduschp-comparison-header__title">{{ col.title }}</p>
          <p class="opc-zeduschp-comparison-header__price">{{ col.price }}</p>
          <i v-if="col.category === planCategory || col.selected" class="zm-icon-ok no-event-btn" tabindex="-1" :aria-label="col.title + ' Selected'" />
          <zm-tooltip v-else-if="col.btn && col.btn.disabledTips" :disabled="!col.btn.disabled">
            <p slot="content" v-html="col.btn.disabledTips" @click="onSubmitFormClick(col, $event)" />
            <div>
              <zm-button data-testid="checkout-button-102" :type="col.btn.type" round :disabled="col.btn.disabled" tabindex="-1" :aria-label="col.btn.ariaLabel" @click="col.btn.callback(col)">{{ col.btn.label }}</zm-button>
            </div>
          </zm-tooltip>
          <zm-button data-testid="checkout-button-103" v-else-if="col.btn" :type="col.btn.type" round :disabled="col.btn.disabled" tabindex="-1" :aria-label="col.btn.ariaLabel" @click="col.btn.callback(col)">{{ col.btn.label }}</zm-button>
          <div v-else class="empty-btn" aria-hidden="true" />
        </div>
      </template>
    </ZoomOneEduPlanComparison>
  </billing-dialog>
</template>

<script>
import { get, find, some } from 'lodash';
import { mapMutations, mapState, mapGetters } from 'vuex';
import { isEduSchCmpPlusContext, ZOOM_MEETING_SUB_TYPES } from '@/plans/ZOOM_MEETING/common/utils';
import Migration from '@/plans/ZOOM_MEETING/dialog/mixins/migration';
import { flowTypes } from '@/common/service/page-config';
import {
  computedPbxAddonPlans,
  computedPbxCallingPlans,
  computedPbxFeaturePlans,
  computedPbxNonBasePlans,
} from '@/common/service/computers/plans/pbx';
import { contactUsPath, planTypes, AUDIO_SUB_TYPES, BILLING_CYCLE, BASE_PLAN_SUB_TYPES} from '@/common/constants';
import { translation } from '@/plugins/i18n';
import { plansof } from '@/utils/chain';
import { ConfirmComponent } from '@/components/common/service-dialog/confirm';
import { isEmptyVal } from '@/utils';
import { computedPbxBasePlans, computedPbxPlans } from '@/common/service/computers/plans/pbx';
import { updateHrefWithQueryParams, URL_PARAM } from '@/utils';
import BillingDialog from '@/components/billing-dialog.vue';

const getDefaultStatusMap = () => {
  return {
    disabled: true,
    link: contactUsPath,
    disabledTips: translation('billing.error.can_not_bought_plan_tips'),
  }
}

const CAMPUS_PLUS_UNAVAILABLE_CURRENCY = ['INR', 'VND', 'BRL', 'MXN', 'IDR', 'PHP', 'KRW', 'MYR'];

export default {
  name: 'ZEduPlusPlanComparison',
  mixins: [ConfirmComponent, Migration],
  components: { BillingDialog },
  props: {
    planCategory: String,
    largeMeetingConfig: Object,
  },
  data() {
    return {
      visible: false,
      enableDocsFeature: window.newBillingConfig.enableDocsFeature,
      showCampusScheduler: window.newBillingConfig.enableCampusScheduler,
      enableDisplayZoomTasksWithoutAccountCheck: window.newBillingConfig.enableDisplayZoomTasksWithoutAccountCheck,
      isEligibleAccount4CustomAvatarFeature: window.newBillingConfig.isEligibleAccount4CustomAvatarFeature,
      enableDisplayZoomWorkFlow: window.newBillingConfig.enableDisplayZoomWorkFlow,
      enableDisplayZoomDrive: window.newBillingConfig.enableDisplayZoomDrive,
    }
  },
  computed: {
    ...mapState({
      planMapFlat: state => state.ShoppingCart.planMapFlat,
      allPlanMap: state => state.ShoppingCart.allPlanMap
    }),
    ...mapGetters([
      'getSelectedPlans',
      'getCurrentBasePlanInfo',
      'migratePlanState',
      'partnerRefer',
      'forNewSubscriptionFlow',
      'getBoughtBasePlanInfo',
      'basicConfig',
      'boughtPlans',
      'subscriptions',
      'plancodeMapSubscriptionDisabled',
      'getTypeGroupSelectedPlans',
      'limitLicenseWithBaseZoneTypes',
      'freeTrialPlans',
    ]),
    disabledSelectedBtn4Partner() {
      return !isEmptyVal(this.partnerRefer) && !this.forNewSubscriptionFlow
    },
    currentBaseCategory() {
      if (isEduSchCmpPlusContext && !this.migratePlanState.is.escp2esc) return ZOOM_MEETING_SUB_TYPES.ZONE_EDU_PLUS;

      const { planType, plans } = this.getCurrentBasePlanInfo;
      if (planType !== planTypes.ZOOM_MEETING || !plans[0]) {
        return '';
      }

      return get(plans, '0.zoomMeetingCategory', '');
    },
    canBuyPlus() {
      const disablePlanCategoryList = get(this.allPlanMap, 'disablePlanCategoryList', []);
      return !!get(this.allPlanMap, 'zoomone.zoom-one-edu-sch-cmp-plus', '') && !disablePlanCategoryList.includes('ZONEDU');
    },
    isMultipleRatePlans() {
      if ([flowTypes.edit].includes(this.$context.flow)) {
        const { plans = [] } = this.getBoughtBasePlanInfo;
        return plans.some(p => p.ratePlans?.length > 1);
      }
      return false;
    },
    meetingPlans() {
      return plansof(this.boughtPlans).type(planTypes.ZOOM_MEETING).get();
    },
    canUpgradePlus() {
      if (!this.basicConfig?.canSelectEduSchoolPlus) {
        return false;
      }

      const audioUnlimited = find(this.boughtPlans, (item) => item.category === AUDIO_SUB_TYPES.UNLIMITED);
      const isBoughtZoneBundlePlan = some(this.boughtPlans, (p) => p.type === planTypes.ZONE_EDU_PLUS);
      const isBoughtZMUnitedPlan = some(this.boughtPlans, (p) => p.type === planTypes.ZOOM_UNITED);
      // zoom phone base plan have been exist audio unlimited plan can not upgrade to z1s biz plus
      if (audioUnlimited || isBoughtZoneBundlePlan || isBoughtZMUnitedPlan) return false;

      const pbxBoughtPlans = computedPbxPlans(this.boughtPlans).filter(p => !p.isFreeTrial);
      const pbxBoughtBasePlan = get(computedPbxBasePlans(pbxBoughtPlans), [0]);
      const pbxBoughtCycle = get(computedPbxNonBasePlans(this.boughtPlans), [0, 'billing_cycle'], '');

      if (pbxBoughtCycle && ![BILLING_CYCLE.ANNUAL, BILLING_CYCLE.MONTHLY].includes(pbxBoughtCycle)) {
        // The current pbx's billing cycle is not Auunal and Monthly, disable upgrade to edu plus.
        return false;
      }

      if (!pbxBoughtPlans.length && !pbxBoughtBasePlan) {
        const pbxFreeTrailBasePlans = computedPbxBasePlans(this.freeTrialPlans);
        // If all pbx plan is free trial, upgrade will disabled when has pbx free trial base plan.
        return !pbxFreeTrailBasePlans?.length;
      }

      return get(pbxBoughtBasePlan, 'isOnline', false) && [BILLING_CYCLE.PAY_AS_YOU_GO, BILLING_CYCLE.MONTHLY, BILLING_CYCLE.ANNUAL].includes(pbxBoughtBasePlan?.billing_cycle);
    },
    isEducationMeeting() {
      return this.currentBaseCategory === BASE_PLAN_SUB_TYPES.EDUCATION
    },
    canUpgradeEduSchoolBasic(){
      if (!this.basicConfig?.canSelectEduSchoolBasic) {
        return false;
      }
      return true
    },
    basicPrice(){
      return this.$money(this.planMapFlat.find((p) => p.category === ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP && p.billing_cycle === BILLING_CYCLE.MONTHLY)?.amount) || '';
    },
    plusPrice(){
      return this.$money(this.planMapFlat.find((p) => p.type === planTypes.ZONE_EDU_PLUS && p.billing_cycle === BILLING_CYCLE.MONTHLY)?.amount) || '';
    },
    configPlusBtn() {
      const flowType = this.$context.flow;
      const defaultStatusMap = getDefaultStatusMap();

      if (CAMPUS_PLUS_UNAVAILABLE_CURRENCY.includes(this.$context.currency)) {
        return defaultStatusMap
      }

      if (this.disabledSelectedBtn4Partner || !this.canBuyPlus) {
        return defaultStatusMap;
      }

      if (this.$context.is.isFreeWinback) {
        return {
          disabled: true,
          link: '/',
        };
      }

      if (this.isMultipleRatePlans) {
        return {
          disabled: true,
          link: '/',
        };
      }

      if (flowTypes.buyNow === flowType) {
        return {
          disabled: false,
          link: `/opc/buy?plan=zopschcmp&from=zopschcmp_zoneduschcmppusca&period=annual`,
        }
      }

      if (flowTypes.add2cart === flowType) {
        if (!this.meetingPlans.length) {
          return {
            disabled: false,
            link: `/opc/buy?type=add&plan=zopschcmp&from=zopschcmp_zoneduschcmppusca&subPlan=newsubs`
          }
        }
      }

      if (flowTypes.basic2pro === flowType) {
        return {
          disabled: false,
          link: `/opc/buy?plan=zopschcmp&subPlan=allSubPlan&type=basic2pro&from=zopschcmp_zoneduschcmppusca&period=annual`,
        }
      }

      // edit me then downgrade
      if (flowTypes.edit === flowType && this.migratePlanState.is.downgrade) {
        return {
          disabled: false,
          link: '/'
        }
      }

      if(this.isEducationMeeting && this.canUpgradePlus){
        return {
          disabled: false,
          link: '/opc/buy/config?plan=zopschcmp&period=annual&from=zoneduschcmp&type=edu2esc',
        }
      }

      // edit others, check whether it can be selected
      if (flowTypes.edit === flowType && this.canUpgradePlus) {
        return {
          disabled: false,
          link: '/',
        }
      }

      if (flowTypes.edu2esc === flowType && this.canUpgradePlus) {
        return {
          disabled: false,
          link: '/',
        }
      }

      return defaultStatusMap;
    },
    configBtn() {
      const flowType = this.$context.flow;
      const defaultStatusMap = getDefaultStatusMap();

      if (this.disabledSelectedBtn4Partner) {
        return defaultStatusMap;
      }

      if (this.$context.is.isFreeWinback) {
        return {
          disabled: true,
          link: '/',
        };
      }

      if (this.isMultipleRatePlans) {
        return {
          disabled: true,
          link: '/',
        };
      }

      if ([flowTypes.buyNow, flowTypes.basic2pro].includes(flowType)) {
        return {
          disabled: false,
          link: `/`,
        }
      }

      if (flowTypes.add2cart === flowType) {
        if (!this.meetingPlans.length) {
          return {
            disabled: false,
            link: `/opc/buy?type=add&plan=zoneduschcmp&from=zoneduschcmp&subPlan=newsubs`
          }
        }
        if (isEduSchCmpPlusContext) {
          return {
            disabled: true,
            link: '/',
          };
        }
        return {
          disabled: false,
          link: `/`,
        }
      }

      // edit me then upgrade to plus
      if (flowTypes.edit === flowType && this.migratePlanState.is.upgrade) {
        return {
          disabled: false,
          link: '/'
        }
      }

      if(this.isEducationMeeting && this.canUpgradeEduSchoolBasic){
        return {
          disabled: false,
          link: '/opc/buy/config?plan=zoneduschcmp&period=annual&from=zoneduschcmp&type=edu2esc',
        }
      }

      if (flowTypes.edu2esc === flowType  && this.canUpgradeEduSchoolBasic) {
        return {
          disabled: false,
          link: '/',
        }
      }

      // when editing others, check whether it can be selected
      if (flowTypes.edit === flowType && isEduSchCmpPlusContext) {
        if (this.basicConfig?.downSellData?.disableDownSell) return defaultStatusMap;

        if (!computedPbxCallingPlans(this.boughtPlans).length && (computedPbxAddonPlans(this.boughtPlans).length || computedPbxFeaturePlans(this.boughtPlans).length)) return defaultStatusMap;

        const boughtPlusPlan = plansof(this.boughtPlans).type(planTypes.ZONE_EDU_PLUS).get()
        const hasPendingPlusPlan = boughtPlusPlan.find(p => this.plancodeMapSubscriptionDisabled[p.plan_code] || false)
        if (hasPendingPlusPlan) return defaultStatusMap

        const hasFreeTrial = this.subscriptions.some(p => p.isFreeTrial)
        if (hasFreeTrial) return defaultStatusMap

        const boughtPlan = plansof(this.boughtPlans).type(planTypes.ZOOM_MEETING).category(ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP).get()
        if (!boughtPlan.length) {
          return {
            disabled: false,
            link: '/'
          }
        }

        return defaultStatusMap;
      }

      return defaultStatusMap;
    },
    colConfig() {
      return [
        {
          title: this.$t('billing.zone_edu_sch.brief_title'),
          key: 'school',
          price: this.$t('billing.common.per_billing_cycle_license.price_monthly', { price: (this.basicPrice|| '') }),
          visible: true,
          category: ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP,
          selected: plansof(this.getSelectedPlans).type(planTypes.ZOOM_MEETING).category(ZOOM_MEETING_SUB_TYPES.ZONE_EDU_SCH_CMP).has(),
          btn: Object.assign({}, {
            callback: this.handleSwitchMeetingPlan.bind(this),
            label: this.$t('billing.common.select'),
            type: 'primary',
            disabled: false,
            link: '/',
            ...this.configBtnModifier(this.configBtn),
          }),
        },
        {
          title: this.$t('billing.zone_edu_plus.brief_title'),
          key: 'schoolPlus',
          price: this.$t('billing.common.per_billing_cycle_license.price_monthly', { price: (this.plusPrice|| '') }),
          visible: true,
          category: ZOOM_MEETING_SUB_TYPES.ZONE_EDU_PLUS,
          selected: plansof(this.getSelectedPlans).type(planTypes.ZONE_EDU_PLUS).has(),
          btn: Object.assign({}, {
            callback: this.handleSwitchMeetingPlan.bind(this),
            label: this.$t('billing.common.select'),
            type: 'primary',
            disabled: false,
            link: '/',
            ...this.configBtnModifier(this.configPlusBtn),
          }),
        },
      ]
    },
    lmConfig() {
      const { school, schoolPlus } = this.largeMeetingConfig || {};

      return {
        school: this.rewriteLargeConfig(school),
        schoolPlus: this.rewriteLargeConfig(schoolPlus)
      }
    }
  },
  methods: {
    ...mapMutations(['removePlan', 'updateSelectPlan', 'setMigratePlanState']),
    rewriteLargeConfig(config) {
      if (typeof config?.callback === 'function') {
        const cb = () => {
          config.callback();
          this.handleCancel();
        };
        return {
          ...config,
          callback: cb,
        };
      }
      return config;
    },
    configBtnModifier(btn){
      if(btn.link == "/") return btn;
      const link = updateHrefWithQueryParams(btn.link, [URL_PARAM.PAGE_TYPE])
      return {
        ...btn,
        link,
      }
    }
  },
}
</script>

<style lang="scss">
@import '@/styles/_variables.scss';
.opc-zeduschp-comparison-dialog__title {
  font-family: $happy-display;
  font-size: 24px;
  font-weight: 600;
  line-height: 29px;
  color: #00031F;
  text-align: center;
}
.opc-zeduschp-comparison-header {
  display: flex !important;
  flex-direction: column;
  align-items: center;
  row-gap: 8px;
  &__title {
    color: #00053D;
    font-size: 16px;
    font-weight: 400;
    line-height: 24px;
  }
  &__price {
    color: #00053D;
    font-size: 14px;
  }
}
</style>
